<template>
  <section class="task-comments" id="task-comments-section">
    <header class="task-comments__header">
      <h2>
        <i class="fas fa-comments" />
        {{ $t('Comments') }}
      </h2>
      <span class="task-comments__count" v-if="totalCount !== null">{{ totalCount }}</span>
    </header>

    <div
      ref="viewport"
      class="task-comments__viewport"
      :class="{ 'task-comments__viewport--loading-older': loadingOlder }"
      @scroll="onScroll"
    >
      <div v-if="loadingOlder" class="task-comments__loader">
        <i class="fas fa-spinner fa-spin mr-1" /> {{ $t('Loading older messages…') }}
      </div>

      <div v-if="initialLoading" class="task-comments__skeleton">
        <div class="sk-row"><div class="sk-avatar" /><div class="sk-bubble medium" /></div>
        <div class="sk-row right"><div class="sk-bubble short" /></div>
        <div class="sk-row"><div class="sk-avatar" /><div class="sk-bubble long" /></div>
      </div>

      <div v-else-if="orderedComments.length === 0" class="task-comments__empty">
        <div class="icon"><i class="far fa-comment-dots" /></div>
        <div class="title">{{ $t('Comments') }}</div>
        <div class="subtitle">{{ $t('No comments yet. Be the first to write.') }}</div>
      </div>

      <template v-else>
        <template v-for="(group, idx) in groupedComments">
          <div
            v-if="group.type === 'day'"
            :key="'day-' + idx"
            class="task-comments__day-divider"
          ><span>{{ group.label }}</span></div>

          <div
            v-else
            :key="'g-' + idx"
            class="task-comments__group"
            :class="group.mine ? 'task-comments__group--mine' : 'task-comments__group--other'"
          >
            <task-comment-message
              v-for="(c, i) in group.items"
              :key="c.__cid || c.id"
              :comment="c"
              :is-mine="group.mine"
              :is-first-of-group="i === 0"
              :show-author-meta="i === 0"
              :can-manage="canManageComment(c, group.mine)"
              :author-name="authorNameFor(c)"
              @retry="retryComment"
              @edit="onEditSubmit"
              @delete="confirmDelete"
            />
          </div>
        </template>
      </template>

      <button
        v-if="newPillCount > 0"
        type="button"
        class="task-comments__new-pill"
        @click="scrollToBottom(true)"
      >
        <i class="fas fa-arrow-down" />
        {{ newPillLabel }}
      </button>
    </div>

    <task-comment-composer v-if="!readOnly" ref="composer" :disabled="!canWrite" @send="sendComment" />
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import { axiosThux as axios } from '@/axiosThux'
import TaskCommentMessage from './TaskCommentMessage.vue'
import TaskCommentComposer from './TaskCommentComposer.vue'

const POLL_INTERVAL = 15000
const PAGE_SIZE = 30
const NEAR_BOTTOM_THRESHOLD = 80

function safeUuid () {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  return 'tmp-' + Math.random().toString(36).slice(2) + Date.now().toString(36)
}

export default {
  name: 'TaskCommentSection',
  components: { TaskCommentMessage, TaskCommentComposer },
  props: {
    taskId: { type: [String, Number], required: true },
    readOnly: { type: Boolean, default: false }
  },
  data () {
    return {
      comments: [],
      totalCount: null,
      initialLoading: true,
      loadingOlder: false,
      hasMoreOlder: false,
      olderNextPage: 2,
      pollHandle: null,
      newPillCount: 0,
      stickyToBottom: true,
      canWrite: true
    }
  },
  computed: {
    ...mapGetters({
      me: 'auth/me',
      profile: 'auth/profile'
    }),
    apiBase () {
      return `/api/v1/task/${this.profile || 'GENERIC'}/task-comment`
    },
    orderedComments () {
      return this.comments.slice().sort((a, b) => {
        const ta = a.date_create ? new Date(a.date_create).getTime() : 0
        const tb = b.date_create ? new Date(b.date_create).getTime() : 0
        return ta - tb
      })
    },
    groupedComments () {
      const out = []
      let lastDay = null
      let currentGroup = null
      for (const c of this.orderedComments) {
        const d = c.date_create ? new Date(c.date_create) : null
        const dayKey = d ? d.toDateString() : 'unknown'
        if (dayKey !== lastDay) {
          out.push({ type: 'day', label: this.dayLabel(d) })
          lastDay = dayKey
          currentGroup = null
        }
        const mine = this.isMineComment(c)
        const key = c.author_username || c.author
        if (!currentGroup || currentGroup.mine !== mine || currentGroup.authorKey !== key) {
          currentGroup = { type: 'group', mine, authorKey: key, items: [] }
          out.push(currentGroup)
        }
        currentGroup.items.push(c)
      }
      return out
    },
    newPillLabel () {
      const n = this.newPillCount
      return n === 1 ? this.$t('1 new message') : this.$t('{n} new messages').replace('{n}', n)
    }
  },
  watch: {
    taskId: {
      immediate: true,
      handler () { this.bootstrap() }
    },
    totalCount (val) {
      if (typeof val === 'number') this.$emit('count-change', val)
    }
  },
  beforeDestroy () { this.stopPolling() },
  mounted () {
    document.addEventListener('visibilitychange', this.onVisibilityChange)
  },
  destroyed () {
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
  },
  methods: {
    isMineComment (c) {
      if (!c || !this.me) return false
      if (this.me.email && c.author_email && c.author_email === this.me.email) return true
      if (this.me.username && c.author_username && c.author_username === this.me.username) return true
      return false
    },
    canManageComment (c, mine) {
      if (this.readOnly) return false
      if (c.__status === 'sending' || c.__status === 'error') return false
      return mine
    },
    authorNameFor (c) {
      if (this.isMineComment(c)) {
        if (this.me) {
          const f = (this.me.first_name || '').trim()
          const l = (this.me.last_name || '').trim()
          return (f + ' ' + l).trim() || this.me.username || this.$t('You')
        }
        return this.$t('You')
      }
      return c.author_full_name || c.author_username || `#${c.author}`
    },
    dayLabel (d) {
      if (!d || isNaN(d.getTime())) return ''
      const today = new Date()
      const yesterday = new Date()
      yesterday.setDate(today.getDate() - 1)
      const isSameDay = (a, b) =>
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
      if (isSameDay(d, today)) return this.$t('Today')
      if (isSameDay(d, yesterday)) return this.$t('Yesterday')
      const dd = String(d.getDate()).padStart(2, '0')
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      return `${dd}/${mm}/${d.getFullYear()}`
    },

    async bootstrap () {
      this.stopPolling()
      this.comments = []
      this.authors = {}
      this.totalCount = null
      this.initialLoading = true
      this.hasMoreOlder = false
      this.olderNextPage = 2
      this.newPillCount = 0
      this.stickyToBottom = true
      try {
        const res = await axios.get(this.apiBase + '/', {
          params: {
            task: this.taskId,
            status: 1,
            per_page: PAGE_SIZE,
            page: 1,
            order_by__: '-date_create'
          }
        })
        const data = res.data || {}
        const results = data.results || data || []
        this.totalCount = (typeof data.count === 'number') ? data.count : results.length
        this.hasMoreOlder = !!data.next
        this.comments = results.slice()
        this.$nextTick(() => this.scrollToBottom(false))
      } catch (e) {
        if (e && e.response && e.response.status === 403) this.canWrite = false
      } finally {
        this.initialLoading = false
        this.startPolling()
      }
    },

    async loadOlder () {
      if (this.loadingOlder || !this.hasMoreOlder) return
      this.loadingOlder = true
      const viewport = this.$refs.viewport
      const prevHeight = viewport ? viewport.scrollHeight : 0
      try {
        const res = await axios.get(this.apiBase + '/', {
          params: {
            task: this.taskId,
            status: 1,
            per_page: PAGE_SIZE,
            page: this.olderNextPage,
            order_by__: '-date_create'
          }
        })
        const data = res.data || {}
        const results = data.results || data || []
        const knownIds = new Set(this.comments.map(c => c.id).filter(x => x !== undefined))
        const fresh = results.filter(c => !knownIds.has(c.id))
        if (fresh.length) {
          this.comments = this.comments.concat(fresh)
        }
        this.hasMoreOlder = !!data.next
        this.olderNextPage += 1
        this.$nextTick(() => {
          if (viewport) viewport.scrollTop = viewport.scrollHeight - prevHeight
        })
      } catch (e) {
        // silent
      } finally {
        this.loadingOlder = false
      }
    },

    startPolling () {
      this.stopPolling()
      this.pollHandle = setInterval(() => {
        if (!document.hidden) this.pollDelta()
      }, POLL_INTERVAL)
    },
    stopPolling () {
      if (this.pollHandle) {
        clearInterval(this.pollHandle)
        this.pollHandle = null
      }
    },
    onVisibilityChange () {
      if (!document.hidden) this.pollDelta()
    },

    async pollDelta () {
      try {
        const res = await axios.get(this.apiBase + '/', {
          params: {
            task: this.taskId,
            status: 1,
            per_page: PAGE_SIZE,
            page: 1,
            order_by__: '-date_create'
          }
        })
        const data = res.data || {}
        const results = data.results || data || []
        if (typeof data.count === 'number') this.totalCount = data.count
        const knownIds = new Set(
          this.comments.filter(c => c.id !== undefined).map(c => c.id)
        )
        const fresh = results.filter(c => !knownIds.has(c.id))
        if (!fresh.length) return
        const wasNearBottom = this.isNearBottom()
        this.comments = this.comments.concat(fresh)
        if (wasNearBottom) {
          this.$nextTick(() => this.scrollToBottom(true))
        } else {
          this.newPillCount += fresh.filter(c => !this.isMineComment(c)).length
        }
      } catch (e) {
        // silent
      }
    },

    isNearBottom () {
      const v = this.$refs.viewport
      if (!v) return true
      return v.scrollHeight - v.scrollTop - v.clientHeight < NEAR_BOTTOM_THRESHOLD
    },
    onScroll () {
      const v = this.$refs.viewport
      if (!v) return
      if (v.scrollTop < 60 && this.hasMoreOlder && !this.loadingOlder) {
        this.loadOlder()
      }
      if (this.isNearBottom()) {
        this.newPillCount = 0
        this.stickyToBottom = true
      } else {
        this.stickyToBottom = false
      }
    },
    scrollToBottom (smooth) {
      const v = this.$refs.viewport
      if (!v) return
      const behavior = smooth ? 'smooth' : 'auto'
      try {
        v.scrollTo({ top: v.scrollHeight, behavior })
      } catch (e) {
        v.scrollTop = v.scrollHeight
      }
      this.newPillCount = 0
    },

    async sendComment (text) {
      if (!text || !this.me) return
      const tmpCid = safeUuid()
      const fullName = ((this.me.first_name || '') + ' ' + (this.me.last_name || '')).trim() || this.me.username || this.me.email
      const optimistic = {
        __cid: tmpCid,
        __status: 'sending',
        id: undefined,
        task: this.taskId,
        author: null,
        author_username: this.me.username,
        author_email: this.me.email,
        author_full_name: fullName,
        text,
        status: 1,
        date_create: new Date().toISOString(),
        date_last_modify: null
      }
      this.comments.push(optimistic)
      this.$nextTick(() => this.scrollToBottom(true))
      try {
        const res = await axios.post(this.apiBase + '/', {
          task: this.taskId,
          text,
          status: 1
        })
        const idx = this.comments.findIndex(c => c.__cid === tmpCid)
        if (idx >= 0) {
          this.$set(this.comments, idx, { ...res.data, __cid: tmpCid })
          if (typeof this.totalCount === 'number') this.totalCount += 1
        }
      } catch (e) {
        const idx = this.comments.findIndex(c => c.__cid === tmpCid)
        if (idx >= 0) {
          this.$set(this.comments, idx, { ...this.comments[idx], __status: 'error', __retryText: text })
        }
      }
    },

    retryComment (failed) {
      if (!failed || failed.__status !== 'error') return
      const text = failed.__retryText || failed.text
      this.comments = this.comments.filter(c => c.__cid !== failed.__cid)
      this.sendComment(text)
    },

    async onEditSubmit ({ id, text }) {
      if (!id || !text) return
      const idx = this.comments.findIndex(c => c.id === id)
      if (idx < 0) return
      const prev = this.comments[idx]
      this.$set(this.comments, idx, { ...prev, text })
      try {
        const res = await axios.patch(`${this.apiBase}/${id}/`, { text })
        this.$set(this.comments, idx, { ...res.data, __cid: prev.__cid })
      } catch (e) {
        this.$set(this.comments, idx, prev)
      }
    },

    async confirmDelete (comment) {
      if (!comment || !comment.id) return
      const ok = window.confirm(this.$t('Delete this comment?'))
      if (!ok) return
      const id = comment.id
      try {
        await axios.patch(`${this.apiBase}/${id}/disable/`, { status: 0 })
        this.comments = this.comments.filter(c => c.id !== id)
        if (typeof this.totalCount === 'number') this.totalCount = Math.max(0, this.totalCount - 1)
      } catch (e) {
        // silent
      }
    }
  }
}
</script>
